import { useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { getSupportTickets, getSupportTicketStats } from '@/features/support/api';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  SupportTicketCategorySchema,
  SupportTicketPrioritySchema,
  SupportTicketStatusSchema,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '@/features/support/types';
import {
  TicketPriorityBadge,
  TicketStatusBadge,
} from '@/features/support/badges';

import { ListPageShell } from '@/components/layout/ListPageShell';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BranchFilter, SearchField } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Headphones, Inbox, ShieldAlert, User } from '@/lib/icons';
import { formatDate, formatDateTimeWithZone } from '@/lib/dates';

const PAGE_SIZE = 25;

/**
 * Filters live in the URL so a queue view is shareable and survives a reload —
 * an operator handing "the urgent unassigned queue" to a colleague should be
 * able to send a link, not a description.
 */
const searchSchema = z.object({
  status: SupportTicketStatusSchema.optional(),
  category: SupportTicketCategorySchema.optional(),
  priority: SupportTicketPrioritySchema.optional(),
  unassigned: z.boolean().optional(),
  branchId: z.string().optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/support/')({
  validateSearch: searchSchema,
  component: SupportTicketsPage,
});

function StatTile({
  label,
  value,
  tone = 'default',
  active,
  onClick,
}: Readonly<{
  label: string;
  value: number;
  tone?: 'default' | 'warn' | 'error';
  active?: boolean;
  onClick?: () => void;
}>) {
  const toneClass =
    tone === 'error'
      ? 'text-[var(--mz-error-700)]'
      : tone === 'warn'
        ? 'text-[var(--mz-warn-700)]'
        : 'text-foreground';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`flex-1 rounded-lg border px-4 py-3 text-left transition-colors ${
        active
          ? 'border-brand-500 bg-brand-50'
          : 'border-border-light bg-card hover:bg-surface-container-low/60'
      }`}
    >
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>
        {value}
      </div>
    </button>
  );
}

function SupportTicketsPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset: clearAll,
    activeCount,
  } = useListFilters({ search, to: '/support' });

  const page = search.page ?? 1;

  const filters = useMemo(
    () => ({
      status: search.status,
      category: search.category,
      priority: search.priority,
      unassignedOnly: search.unassigned,
      branchId: search.branchId,
      search: debouncedSearch,
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    [
      search.status,
      search.category,
      search.priority,
      search.unassigned,
      search.branchId,
      debouncedSearch,
      page,
    ]
  );

  const {
    data: ticketsResult,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['support-tickets', filters],
    queryFn: () => getSupportTickets(filters),
    staleTime: 15_000,
  });

  const { data: statsResult } = useQuery({
    queryKey: ['support-ticket-stats'],
    queryFn: () => getSupportTicketStats(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const tickets = ticketsResult?.succeeded ? (ticketsResult.value ?? []) : [];
  const stats = statsResult?.succeeded ? statsResult.value : undefined;
  const failed = isError || (ticketsResult && !ticketsResult.succeeded);

  // Toggle: clicking the active filter clears it, so a filter can always be
  // undone with the control that set it.
  const setFilter = <K extends keyof typeof search>(
    key: K,
    value: (typeof search)[K]
  ) => setFilters({ [key]: search[key] === value ? undefined : value });

  const hasFilters =
    activeCount > 0 ||
    !!search.category ||
    !!search.priority ||
    !!search.unassigned;

  return (
    <ListPageShell
      title="Support Tickets"
      description="Customer support requests raised from the Nuur mobile app and USSD."
      actions={
        stats ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Headphones className="h-4 w-4" />
            <span className="tabular-nums">{stats.total} total</span>
          </div>
        ) : null
      }
      filters={
        <>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search ticket number, MSISDN, IMEI or subject…"
            isPending={isSearchPending}
            className="min-w-[260px] flex-1"
          />

          <select
            value={search.status ?? ''}
            onChange={(e) =>
              setFilter(
                'status',
                (e.target.value || undefined) as SupportTicketStatus | undefined
              )
            }
            aria-label="Filter by status"
            className="h-9 rounded-md border border-border bg-card px-3 text-sm"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={search.category ?? ''}
            onChange={(e) =>
              setFilter(
                'category',
                (e.target.value || undefined) as
                  | SupportTicketCategory
                  | undefined
              )
            }
            aria-label="Filter by category"
            className="h-9 rounded-md border border-border bg-card px-3 text-sm"
          >
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={search.priority ?? ''}
            onChange={(e) =>
              setFilter(
                'priority',
                (e.target.value || undefined) as
                  | SupportTicketPriority
                  | undefined
              )
            }
            aria-label="Filter by priority"
            className="h-9 rounded-md border border-border bg-card px-3 text-sm"
          >
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>

          <BranchFilter
            value={search.branchId}
            onChange={(branchId) => setFilters({ branchId })}
            className="h-9 w-[180px]"
          />

          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear filters
            </Button>
          ) : null}
        </>
      }
      hasData
    >
      {/* Queue counters double as one-click filters — the number an operator
          is looking at is the queue they want to open. */}
      {stats ? (
        <div className="flex flex-wrap gap-3 border-b border-border-light px-4 py-4 md:px-5">
          <StatTile
            label="Open"
            value={stats.open}
            tone="warn"
            active={search.status === 'open'}
            onClick={() => setFilter('status', 'open')}
          />
          <StatTile
            label="In progress"
            value={stats.inProgress}
            active={search.status === 'in_progress'}
            onClick={() => setFilter('status', 'in_progress')}
          />
          <StatTile
            label="Awaiting customer"
            value={stats.awaitingCustomer}
            active={search.status === 'awaiting_customer'}
            onClick={() => setFilter('status', 'awaiting_customer')}
          />
          <StatTile
            label="Unassigned"
            value={stats.unassigned}
            tone="warn"
            active={!!search.unassigned}
            onClick={() => setFilter('unassigned', search.unassigned ? undefined : true)}
          />
          <StatTile
            label="Urgent"
            value={stats.urgent}
            tone="error"
            active={search.priority === 'urgent'}
            onClick={() => setFilter('priority', 'urgent')}
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-2 p-4 md:p-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : failed ? (
        <div className="p-4 md:p-5">
          <ApiErrorCard
            title="Failed to load support tickets"
            error={
              new Error(
                ticketsResult?.errors?.[0] ??
                  'The support service did not respond. Please try again.'
              )
            }
            onRetry={() => refetch()}
          />
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <div className="text-sm font-semibold text-foreground">
            {hasFilters ? 'No tickets match these filters' : 'No support tickets yet'}
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            {hasFilters
              ? 'Try clearing a filter or widening your search.'
              : 'Tickets raised by customers from the Nuur mobile app will appear here.'}
          </p>
          {hasFilters ? (
            <Button variant="outline" size="sm" onClick={clearAll}>
              Clear filters
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Raised</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/support/$ticketId',
                      params: { ticketId: ticket.id },
                    })
                  }
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-brand-700">
                        {ticket.ticketNumber}
                      </span>
                      <TicketPriorityBadge priority={ticket.priority} />
                    </div>
                    <div className="mt-0.5 max-w-[280px] truncate text-sm text-foreground">
                      {ticket.subject}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-foreground">
                      {ticket.customerName ?? '—'}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {ticket.msisdn ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {CATEGORY_LABELS[ticket.category] ?? ticket.category}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-foreground">
                      {ticket.deviceName ?? '—'}
                    </div>
                    {ticket.deviceStatus ? (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {ticket.deviceStatus === 'locked' ? (
                          <ShieldAlert className="h-3 w-3 text-[var(--mz-error-700)]" />
                        ) : null}
                        {ticket.deviceStatus}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <TicketStatusBadge status={ticket.status} />
                  </TableCell>
                  <TableCell>
                    {ticket.assignedToName ? (
                      <div className="flex items-center gap-1.5 text-sm">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        {ticket.assignedToName}
                      </div>
                    ) : (
                      <span className="text-xs font-medium text-[var(--mz-warn-700)]">
                        Unassigned
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    <span title={formatDateTimeWithZone(ticket.createdAt)}>
                      {formatDate(ticket.createdAt)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Offset paging with no total count from the API: a full page means
              there may be more, so Next is offered only then. */}
          <div className="flex items-center justify-between border-t border-border-light px-4 py-3 md:px-5">
            <span className="text-sm text-muted-foreground">
              Showing {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
              {page > 1 ? ` · page ${page}` : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setFilters({ page: page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={tickets.length < PAGE_SIZE}
                onClick={() => setFilters({ page: page + 1 })}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </ListPageShell>
  );
}
